import React, { useCallback, useMemo, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useGetPage } from 'hooks/useGetPage';
import { pageIds } from 'utilities/constants';
import { twJoin } from 'tailwind-merge';
import WalletPayment from 'components/stripe/WalletPayment';
import Alert from 'components/alertComponent';
import { Benefit } from '../my-profile/components/providerComponents/membership/components/Benefits';
import AddPayMethodForm from '../my-profile/components/providerComponents/membership/components/AddPayMethodForm';
import Loader from 'components/loaderComponent';
import {
  useGetMyBillingPlanQuery,
  useGetThirdPartyConfigQuery,
} from 'graphql/generated/hasura';
import { useCustomToast } from 'layout/useToast';
import { sanitize } from 'utilities/sanitizer';

const replace = (template: string, amount: number) => {
  const token = '{cost}';
  const replacements = [amount, amount / 3];
  let i = 0;
  return template.replace(new RegExp(token, 'g'), () => {
    return replacements[i++]?.toString() || '';
  });
};

const StartTrial = () => {
  const { data: locale, loading } = useGetPage({
    locale: 'en',
    pageId: pageIds.MEMBERSHIP,
  });
  const { addToast } = useCustomToast();
  const handleToast = useCallback(
    () =>
      addToast({
        message: 'Your 30-day free trial is now active!',
        type: 'success',
      }),
    [addToast],
  );

  const { data } = useGetThirdPartyConfigQuery({
    context: {
      ignoreGlobalErrors: true,
    },
  });
  const key = useMemo(
    () => data?.frontEndAuthConfigs?.stripe?.publicKey,
    [data],
  );
  const stripePromise = loadStripe(key as string);

  const { data: currentPlan } = useGetMyBillingPlanQuery();

  const plan = useMemo(() => currentPlan?.getMyBillingPlan, [currentPlan]);

  const [error, setError] = useState<string | null>(null);

  if (!locale || loading) {
    return <Loader />;
  }

  if (!data) {
    return <Loader />;
  }

  return (
    <div className="grid grid-cols-12 gap-30">
      <div className="desktop:col-span-7 col-span-12">
        <div className="p-30 bg-white rounded-10 flex flex-col justify-between gap-5 w-full">
          <h2 className="text-h1 text-neutral-800 font-medium">
            {'Enjoy 30 Days Free'}
          </h2>
          <p className="font-medium text-neutral-800 text-base">
            {
              'We’re excited to offer you a 30-day free trial to experience our telemedicine platform with no risk. Rest assured, we do not store your credit card information on our site. After your trial, payments are collected securely every 90 days through our trusted payment processor. Our platform is HIPAA-compliant and integrates seamlessly with the tools you rely on:'
            }
          </p>

          <div className="flex flex-col gap-1.5">
            {(locale?.options ?? []).map((text: string) => (
              <Benefit key={text} text={text} />
            ))}
          </div>

          <Alert
            type="info-blue"
            iconClassName="hidden"
            customContent={
              <div>
                <p className="text-med-gray-3 font-medium text-base">
                  Your subscription will be activated for
                  <span className="text-neutral-800 font-semibold mx-1">
                    3 months
                  </span>
                  after the 30-day free trial.
                </p>
                <p
                  className="text-med-gray-3 font-medium text-base"
                  dangerouslySetInnerHTML={{
                    __html: sanitize(
                      replace(
                        'You will be billed automatically <span class="text-neutral-800 font-semibold">${cost}</span> (${cost} per month).',
                        plan?.amount ?? 0,
                      ),
                    ),
                  }}
                />
              </div>
            }
          />
        </div>
      </div>
      <div className="desktop:col-span-5 col-span-12">
        <div className="flex flex-col gap-30 mt-2.5">
          <div className="p-30 bg-white rounded-10 flex flex-col justify-between gap-5 w-full">
            <h3 className="text-h3 text-neutral-800 font-semibold flex items-center">
              <span>{locale?.paymentMethod}</span>
            </h3>
            <Elements
              stripe={stripePromise}
              options={{
                mode: 'setup',
                currency: 'usd',
                amount: 0,
              }}
            >
              <div className={twJoin('flex flex-col w-full')}>
                <AddPayMethodForm
                  submitTitle="Activate Free Trial"
                  setErrorMessage={setError}
                  callback={handleToast}
                />
                <WalletPayment callback={handleToast} />
              </div>
            </Elements>
          </div>
        </div>

        {error && (
          <Alert
            type="warning"
            desktopItemsAlign="desktop:items-start"
            text="There was an error processing your payment. Please try again, or contact us if you continue to have problems"
          />
        )}
      </div>
    </div>
  );
};

export default StartTrial;
